import React, { JSX } from "react";
import { ButtonStyle } from "components/buttons/button/button";
import { Heading, HeadingPriority, HeadingSize } from "components/typography/heading/heading";
import { Modal, ModalAction } from "components/modal/modal";
import {
    Paragraph,
    ParagraphSize,
    ParagraphStyle,
} from "components/typography/paragraph/paragraph";
import {
    PatchProductServiceParams,
    PatchRoutes,
    ProductService,
} from "utilities/services/products/product-service";
import { ProductRecord } from "models/view-models/products/product-record";
import { ToastManager } from "utilities/toast/toast-manager";
import { TrainingType } from "models/enumerations/courses/training-type";
import { sitemap } from "sitemap";
import { useNavigate } from "utilities/hooks/navigation/use-navigate";
import { t } from "utilities/localization/t";
import "./product-reactivation-modal.scss";

// -------------------------------------------------------------------------------------------------
// #region Interfaces
// -------------------------------------------------------------------------------------------------

interface ProductReactivationModalProps {
    product: ProductRecord;
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

// #endregion Interfaces

// -------------------------------------------------------------------------------------------------
// #region Constants
// -------------------------------------------------------------------------------------------------

const CSS_CLASS_NAME: string = "product-reactivation-modal";

// #endregion Constants

// -------------------------------------------------------------------------------------------------
// #region Component
// -------------------------------------------------------------------------------------------------

const ProductReactivationModal: React.FC<ProductReactivationModalProps> = ({
    product,
    open,
    setOpen,
}): JSX.Element => {
    const navigate = useNavigate();
    const { patch: productReactivate } = ProductService.usePatch();

    const modalActionArray: ModalAction[] = [
        {
            buttonText: t("cancel"),
            onClick: () => setOpen(false),
            style: ButtonStyle.Secondary,
        },
        {
            buttonText: t("reactivateProduct"),
            onClick: () => reactivateProduct(),
            style: ButtonStyle.Primary,
        },
    ];

    const reactivateProduct = async (): Promise<boolean> => {
        if (product.id == null) {
            return false;
        }
        setOpen(false);
        try {
            const patchProductServiceParams: PatchProductServiceParams = {
                productId: product.id,
                patchRoute: PatchRoutes.Reactivate,
            };

            const patchProductResponse = await productReactivate(patchProductServiceParams);

            const patchProductResult = patchProductResponse?.result;
            if (patchProductResult?.resultObject == null || patchProductResult.hasErrors()) {
                alert(t("reactivatingProductFailed"));
            }
            navigate(sitemap.admin.product.list);
            ToastManager.success(t("productIsNowReactivated"));
        } catch {
            ToastManager.error(t("failedToReactivateProduct"));
            return false;
        }
        return true;
    };

    return (
        <Modal
            cssClassName={CSS_CLASS_NAME}
            isOpen={open}
            onModalClose={() => setOpen(false)}
            title={t("reactivateProduct")}
            actions={modalActionArray}
            modalStyle={""}>
            <Heading
                cssClassName={`${CSS_CLASS_NAME}__section`}
                size={HeadingSize.XSmall}
                priority={HeadingPriority.H5}>
                {t("youAreReactivatingThisProduct")}
            </Heading>
        </Modal>
    );
};

// #endregion Component

// -------------------------------------------------------------------------------------------------
// #region Exports
// -------------------------------------------------------------------------------------------------

export { ProductReactivationModal };

// #endregion Exports
